import {Injectable} from "@angular/core";
import Pusher from "pusher-js";
import Echo, {Channel} from "laravel-echo";
import {environment} from "@environment/environment";
import {AuthApi} from "@modules/auth/api/auth.api";
import {NGXLogger} from "ngx-logger";
import {InAppNotificationLog} from "@modules/in-app-notification-log/models/in-app-notification-log.model";
import {AppEventEnum, InAppNotificationLogType, PaymentStatusEnum} from "@generated/graphql";
import {
  inAppNotificationLogModelFactory
} from "@modules/in-app-notification-log/factories/in-app-notification-log.factory";

export type BroadcastHandler<T = any> = (payload: T) => void;

export type AppEventMap = {
  [AppEventEnum.InAppNotificationCreated]: {
    BE: { log: InAppNotificationLogType };
    FE: { log: InAppNotificationLog };
  };
  [AppEventEnum.PaymentStatusUpdated]: {
    BE: {
      paymentId: string;
      orderId?: string | null;
      status?: PaymentStatusEnum | null;
      outcome: 'succeeded' | 'failed' | 'submitted' | 'approved' | 'rejected';
      message?: string | null;
      updatedAt?: string | null;
    };
    FE: {
      paymentId: string;
      orderId?: string | null;
      status?: PaymentStatusEnum | null;
      outcome: 'succeeded' | 'failed' | 'submitted' | 'approved' | 'rejected';
      message?: string | null;
      updatedAt?: string | null;
    };
  };
};

export type AppEvent = {
  [K in keyof AppEventMap]: AppEventMap[K]['FE'];
};

export type AppBEEvent = {
  [K in keyof AppEventMap]: AppEventMap[K]['BE'];
};

export interface TypedChannel<TEvents extends Record<string, any>> extends Channel {
  listen<K extends keyof TEvents & string>(
    event: `.${K}`,
    callback: BroadcastHandler<TEvents[K]>
  ): this;

  stopListening<K extends keyof TEvents & string>(
    event: `.${K}`,
    callback?: Function
  ): this;
}

export interface EventSubscription {
  unsubscribe: () => void;
  leaveChannel: () => void;
}

// Please do not expose channel instance to outside, we should keep it in here
@Injectable({
  providedIn: 'root'
})
export class PusherService {
  private _echo: Echo | null = null;
  private readonly channelSubscriptionCounts = new Map<string, number>();

  private globalEventHandler: (eventName: string, data: any) => void;

  get echo(): Echo | null {
    return this._echo;
  }

  private get notificationFactory() {
    return inAppNotificationLogModelFactory
  }

  constructor(
    private readonly authApi: AuthApi,
    private readonly logger: NGXLogger
  ) {
    window["Pusher"] = Pusher;
  }

  setup() {
    this._echo ??= this.connect();
  }

  disconnect(): void {
    if (this._echo) {
      if (this.globalEventHandler) {
        this._echo.connector.pusher.connection.unbind_global(this.globalEventHandler);
        this.logger.debug("[Pusher] Unbound global event handler");
      }

      this._echo.connector.pusher.connection.unbind_all();
      this._echo.disconnect();
      this._echo = null;
      this.channelSubscriptionCounts.clear();
      this.logger.debug("[Pusher] Disconnected from server");
    }
  }

  subscribe<K extends keyof AppEvent>(
    channelName: string,
    event: K,
    cb: BroadcastHandler<AppEvent[K]>
  ): EventSubscription {
    const channel = this.privateChannel(channelName);
    const eventName = `.${event}` as const;

    const listener = (payload: AppBEEvent[K]) => {
      const transformers: {
        [E in keyof AppEvent]?: (payload: AppBEEvent[E]) => AppEvent[E];
      } = {
        [AppEventEnum.InAppNotificationCreated]: payload => ({
          log: this.notificationFactory.tryCreating(payload.log),
        }),
      };

      const transformer = transformers[event] as ((p: AppBEEvent[K]) => AppEvent[K]) | undefined;

      const transformed = transformer ? transformer(payload) : (payload as unknown as AppEvent[K]);

      cb(transformed);
    };

    channel.listen(eventName, listener);
    this.incrementChannelSubscriptionCount(channelName);

    this.logger.info(`[Pusher] Subscribed to: ${channelName}.${String(event)}`);

    let isClosed = false;
    const close = () => {
      if (isClosed) {
        return;
      }

      isClosed = true;
      this.unsubscribe(channelName, event, listener);
    };

    return {
      unsubscribe: close,
      leaveChannel: close,
    };
  }

  private unsubscribe<K extends keyof AppEvent>(
    channelName: string,
    event: K,
    listener: BroadcastHandler<AppBEEvent[K]>
  ): void {
    this.privateChannel(channelName).stopListening(`.${event}`, listener);
    this.decrementChannelSubscriptionCount(channelName);

    this.logger.info(`[Pusher] Unsubscribed: ${channelName}.${event}`);
  }

  private incrementChannelSubscriptionCount(channelName: string): void {
    this.channelSubscriptionCounts.set(
      channelName,
      (this.channelSubscriptionCounts.get(channelName) ?? 0) + 1,
    );
  }

  private decrementChannelSubscriptionCount(channelName: string): void {
    const currentCount = this.channelSubscriptionCounts.get(channelName) ?? 0;

    if (currentCount <= 1) {
      this.channelSubscriptionCounts.delete(channelName);
      this._echo.leave(channelName);
      this.logger.info(`[Pusher] Left channel: ${channelName}`);
      return;
    }

    this.channelSubscriptionCounts.set(channelName, currentCount - 1);
  }

  private privateChannel(channelName: string) {
    const channel = this._echo.private(channelName) as Channel;

    return channel as TypedChannel<AppBEEvent>;
  }

  private connect() {
    try {
      const echo = this.createEcho();

      this.globalEventHandler = (eventName: string, data: any) => {
        this.logger.debug(`[Pusher] ${eventName}`, data);
      };

      const pusher = echo.connector.pusher as Pusher

      pusher.connection.bind_global(this.globalEventHandler);

      pusher.connection.bind("connected", () => {
        this.logger.debug("[Pusher] is connected to server!");
      });

      pusher.connection.bind("disconnected", () => {
        this.logger.warn("[Pusher] connection lost!");
      });

      this._echo = echo;
      window["Echo"] = echo;
      return echo;
    } catch (e) {
      this.logger.error("[Pusher] Cannot connect!", e);
      return null;
    }
  }

  private createEcho() {
    return new Echo({
      broadcaster: "pusher",
      key: environment.pusher.appKey,
      cluster: environment.pusher.cluster,
      forceTLS: true,
      authorizer: (channel: any, options: any) => ({
        authorize: async (socketId: string, callback: any) => {
          try {
            const response = await this.authApi
              .broadCastingAuth$({socketId, channelName: channel.name})
              .toPromise();
            callback(false, response);
          } catch (error) {
            callback(true, error);
          }
        },
      }),
    });
  }
}
